import React, { useContext } from 'react';

import { BiArrowFromTop } from 'react-icons/bi';
import styled, { ThemeContext } from 'styled-components';

import { Theme } from 'contexts/theme';

import {
    GetMicrostoreSiteQuery,
    GetMicrostoreSitesWithLocationsInfoQuery,
    MicrostoreLocationTemplateFragmentFragment,
    ProductArrangementFragmentFragment,
    ProductArrangementReassortmentType,
    ProductFlow,
} from 'data/__generated__';

import {
    MicrostoreColumnFormValues,
    microstoreColumnSomeLocationHelper,
    MicrostoreLocationFormValues,
    MicrostoreProductStockInfoFlat,
    MicrostoreProductsStockInfoFormValues,
    MicrostoreShelfFormValues,
} from './FormHelper/DataMapper';

import { BirthdayDate, dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { getReplacementProductStockInLocation, getStockToRemoveInLocation } from './FormHelper/stockInLocationHelper';

function getProductInfoInArrangement({
    productId,
    microstoreLocationTemplate,
    productArrangement,
}: {
    productId: string | null;
    microstoreLocationTemplate: MicrostoreLocationTemplateFragmentFragment | null;
    productArrangement: ProductArrangementFragmentFragment | null;
}): Omit<ProductArrangementFragmentFragment['productIdsWithStockMax'][number], '__typename'> | null | null {
    if (!productId) {
        return null;
    }

    // if a reassortment type is setup use stockMax of template instead of productArrangement
    if (
        microstoreLocationTemplate &&
        microstoreLocationTemplate.reassortmentType &&
        microstoreLocationTemplate.stockMax
    ) {
        return {
            productId,
            stockMax: microstoreLocationTemplate.stockMax ?? 0,
        };
    }

    if (productArrangement) {
        return (
            productArrangement.productIdsWithStockMax.find(
                (productIdWithStockMax) => productId === productIdWithStockMax.productId,
            ) ?? null
        );
    }

    return null;
}

function getStockMaxFromProductInfoInArrangement({
    productInfoInArrangement,
}: {
    productInfoInArrangement: Omit<
        ProductArrangementFragmentFragment['productIdsWithStockMax'][number],
        '__typename'
    > | null;
}): number | null {
    return productInfoInArrangement?.stockMax ?? null;
}

function getExpiryDateColor(expiryDate: string, theme: Theme) {
    const today = new Date();
    if (formatDateAsAnniversary({ dateTime: today }) === expiryDate) {
        return theme.warningColor;
    }

    if (today > dateFromString(expiryDate)) {
        return theme.errorColor;
    }

    return 'inherit';
}

function getExpiryDateDisplay(
    expiryDateRecords: MicrostoreProductStockInfoFlat['expiryDateRecordsWarehouse'],
    theme: any,
) {
    if (!expiryDateRecords.length) {
        return {
            label: null,
            color: null,
        };
    }
    const sortedExpiryDateRecords = [...expiryDateRecords].sort((expiryDateRecordA, expiryDateRecordB) =>
        dateFromString(expiryDateRecordA.expiryDate as string) < dateFromString(expiryDateRecordB.expiryDate as string)
            ? -1
            : 1,
    );

    return {
        label: sortedExpiryDateRecords[0].expiryDate + (sortedExpiryDateRecords.length > 1 ? '...' : ''),
        color: getExpiryDateColor(sortedExpiryDateRecords[0].expiryDate, theme),
    };
}

function getLocationBackgroundColor({
    column,
    displayOnlyTomorrowForPrint,
    flow,
    location,
    switchColumn,
    trackExpiryDate,
}: {
    column: MicrostoreColumnFormValues;
    displayOnlyTomorrowForPrint: boolean;
    flow: ProductFlow | undefined;
    location: MicrostoreLocationFormValues;
    switchColumn: MicrostoreColumnFormValues | null;
    trackExpiryDate: boolean;
}) {
    if (location.replacementProductId === null) {
        return 'inherit';
    }

    const isPresentToday = microstoreColumnSomeLocationHelper({
        microstoreColumnFormValues: column,
        someFunction: (otherLocation) => location.replacementProductId === otherLocation.productId,
    });
    const isPresentInSwitchToday =
        switchColumn &&
        microstoreColumnSomeLocationHelper({
            microstoreColumnFormValues: switchColumn,
            someFunction: (otherLocation) => location.replacementProductId === otherLocation.productId,
        });

    const isRelocated =
        location.replacementProductId !== location.productId && (isPresentToday || isPresentInSwitchToday);

    if (isRelocated) {
        return '#fffb00c8';
    }

    if (displayOnlyTomorrowForPrint && trackExpiryDate && flow !== ProductFlow.Fresh) {
        // we use colors instead of theme because when printing you don't want to be in a darkmode
        return '#e181ff60';
    }

    if (location.replacementProductId === location.productId) {
        return 'inherit';
    }

    return '#1bbe4f5a';
}

function getTargetStockDisplay({ targetStock, stockMax }: { targetStock: number | null; stockMax: number | null }): {
    label: string;
    isError: boolean;
} {
    if (stockMax === null) {
        return { label: 'Auto Stock Cible ?', isError: true };
    }
    if (targetStock === null) {
        return { label: `Auto Stock Cible ${stockMax}`, isError: false };
    }
    return { label: `Auto Stock Cible : empl. ${targetStock} · templ. ${stockMax}`, isError: false };
}

export const TomorrowShelf = ({
    allSiteLocations,
    canBeUpdated = true,
    displayOnlyTomorrowForPrint,
    hideTomorrowInfo = false,
    isToday,
    microstoreProductsStockInfo,
    nextSiteDeliveryDate,
    earliestDLCDateToKeepForStockCalculation,
    selectedColumn,
    selectedLocationPosition,
    selectedShelfIndex,
    setIsToday,
    setSelectedLocationPosition,
    setSelectedShelfIndex,
    shelf,
    shelfIndex,
    site,
    switchColumn,
    updateSelectedLocation,
}: {
    allSiteLocations: MicrostoreLocationFormValues[];
    canBeUpdated?: boolean;
    displayOnlyTomorrowForPrint: boolean;
    hideTomorrowInfo?: boolean;
    isToday: boolean;
    microstoreProductsStockInfo: MicrostoreProductsStockInfoFormValues | null;
    nextSiteDeliveryDate: BirthdayDate | null;
    earliestDLCDateToKeepForStockCalculation: BirthdayDate | null;
    selectedColumn: MicrostoreColumnFormValues;
    selectedLocationPosition: { column: number; row: number } | null;
    selectedShelfIndex: number | null;
    setIsToday: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocationPosition: React.Dispatch<React.SetStateAction<{ column: number; row: number } | null>>;
    setSelectedShelfIndex: React.Dispatch<React.SetStateAction<number | null>>;
    shelf: MicrostoreShelfFormValues;
    shelfIndex: number;
    site: GetMicrostoreSiteQuery['siteWithLocationInfo'] | GetMicrostoreSitesWithLocationsInfoQuery['sites'][number];
    switchColumn: MicrostoreColumnFormValues | null;
    updateSelectedLocation: (values: Partial<MicrostoreLocationFormValues>) => void;
}) => {
    const theme = useContext(ThemeContext);

    const canSelectLocation =
        canBeUpdated &&
        (!selectedColumn.removalDate || (!!nextSiteDeliveryDate && nextSiteDeliveryDate < selectedColumn.removalDate));

    return (
        <Shelf>
            <ShelfHeader>J+1</ShelfHeader>
            <ShelfBody>
                {shelf.locationTable.map((locationRow, locationRowIndex) => {
                    return (
                        <LocationRow key={locationRowIndex}>
                            {locationRow.map((location, locationColumnIndex) => {
                                if (hideTomorrowInfo) {
                                    return (
                                        <EmptyLocation
                                            key={locationColumnIndex}
                                            $canSelectLocation={canSelectLocation}
                                            $isSelected={false}
                                            $backgroundColor="inherit"
                                        />
                                    );
                                }

                                const { microstoreLocationTemplate, replacementProductId, productId, targetStock } =
                                    location;

                                const replacementProduct = microstoreProductsStockInfo?.find(
                                    ({ _id }) => _id === replacementProductId,
                                );

                                const { label: expiryDateLabel, color: expiryDateColor } = getExpiryDateDisplay(
                                    replacementProduct?.expiryDateRecordsWarehouse ?? [],
                                    theme,
                                );
                                const locationBackgroundColor = getLocationBackgroundColor({
                                    displayOnlyTomorrowForPrint,
                                    flow: replacementProduct?.flow,
                                    location,
                                    column: selectedColumn,
                                    switchColumn,
                                    trackExpiryDate: !!replacementProduct?.trackExpiryDate,
                                });

                                const replacementProductStockInLocation = getReplacementProductStockInLocation({
                                    locationId: location._id,
                                    allSiteLocations,
                                    microstoreProductsStockInfo,
                                    replacementProductId: replacementProductId || null,
                                });

                                const stockToRemoveInLocation = getStockToRemoveInLocation({
                                    locationId: location._id,
                                    allSiteLocations,
                                    microstoreProductsStockInfo,
                                    earliestDLCDateToKeepForStockCalculation,
                                    replacementProductId: replacementProductId || null,
                                });

                                const isLocationSelected =
                                    canBeUpdated &&
                                    shelfIndex === selectedShelfIndex &&
                                    locationColumnIndex === selectedLocationPosition?.column &&
                                    locationRowIndex === selectedLocationPosition?.row &&
                                    !isToday;

                                const stockAvailableForReassortment = replacementProduct
                                    ? replacementProduct.stockAvailableWarehouse -
                                      replacementProduct.stockUsedInMicrostore -
                                      replacementProduct.stockUsedInOtherMicrostores
                                    : 0;

                                const usedReassortmentType = microstoreLocationTemplate.productArrangement
                                    ? microstoreLocationTemplate.productArrangement.reassortmentType
                                    : microstoreLocationTemplate.reassortmentType;

                                const productInfoInArrangement = getProductInfoInArrangement({
                                    microstoreLocationTemplate,
                                    productArrangement: microstoreLocationTemplate.productArrangement || null,
                                    productId: productId || null,
                                });
                                const replacementProductStockMax =
                                    getStockMaxFromProductInfoInArrangement({
                                        productInfoInArrangement,
                                    }) ?? 0;
                                const targetStockDisplay = getTargetStockDisplay({
                                    targetStock: targetStock ?? null,
                                    stockMax: getStockMaxFromProductInfoInArrangement({ productInfoInArrangement }),
                                });

                                const isDoubleCoil = replacementProduct?.isDoubleCoil;

                                return (
                                    <Location
                                        key={locationColumnIndex}
                                        $canSelectLocation={canSelectLocation}
                                        $isSelected={isLocationSelected}
                                        $hasStockToAdd={!!location.stockToAdd}
                                        $isDoubleCoil={isDoubleCoil}
                                        onClick={(event) => {
                                            if (!canSelectLocation) {
                                                return;
                                            }
                                            event.stopPropagation();
                                            setSelectedShelfIndex(shelfIndex);
                                            setSelectedLocationPosition({
                                                column: locationColumnIndex,
                                                row: locationRowIndex,
                                            });
                                            setIsToday(false);
                                        }}
                                        $backgroundColor={locationBackgroundColor}
                                    >
                                        {replacementProduct ? (
                                            <>
                                                <LocationProductName>
                                                    {isDoubleCoil
                                                        ? `[DOUBLE SPIRE] - ${replacementProduct.name}`
                                                        : replacementProduct.name}
                                                </LocationProductName>
                                                {/* web section */}
                                                {expiryDateLabel ? (
                                                    <LocationProductInfoRow $hideOnPrint>
                                                        <LocationProductInfo color={expiryDateColor}>
                                                            {expiryDateLabel}
                                                        </LocationProductInfo>
                                                    </LocationProductInfoRow>
                                                ) : null}

                                                {!replacementProduct.isWithoutStock ? (
                                                    <>
                                                        <LocationProductInfoRow $hideOnPrint>
                                                            <LocationProductInfo
                                                                color={
                                                                    stockAvailableForReassortment > 0
                                                                        ? 'inherit'
                                                                        : theme?.errorColor
                                                                }
                                                            >
                                                                Disp&nbsp;
                                                                {stockAvailableForReassortment}
                                                            </LocationProductInfo>
                                                            {stockToRemoveInLocation !== 0 ? (
                                                                <>
                                                                    <LocationProductInfo> | </LocationProductInfo>
                                                                    <LocationProductInfo color={theme?.errorColor}>
                                                                        S-:&nbsp;{stockToRemoveInLocation}
                                                                    </LocationProductInfo>
                                                                </>
                                                            ) : null}
                                                            <LocationProductInfo> | </LocationProductInfo>
                                                            <LocationProductInfo>
                                                                Liv&nbsp;+{location.stockToAdd}
                                                            </LocationProductInfo>
                                                            <LocationProductInfo> | </LocationProductInfo>
                                                            <LocationProductInfo
                                                                $isBold
                                                                color={
                                                                    replacementProductStockInLocation +
                                                                        location.stockToAdd >
                                                                    0
                                                                        ? 'inherit'
                                                                        : theme?.errorColor
                                                                }
                                                            >
                                                                Fin&nbsp;
                                                                {microstoreProductsStockInfo
                                                                    ? replacementProductStockInLocation +
                                                                      location.stockToAdd -
                                                                      stockToRemoveInLocation
                                                                    : 'Loading'}
                                                            </LocationProductInfo>
                                                        </LocationProductInfoRow>
                                                        {/* printer section */}
                                                        <LocationProductInfoRow $hideOnWeb>
                                                            <LocationProductInfo $isBold>
                                                                ST:&nbsp;
                                                                {replacementProductStockInLocation +
                                                                    location.stockToAdd -
                                                                    stockToRemoveInLocation}
                                                            </LocationProductInfo>
                                                        </LocationProductInfoRow>
                                                    </>
                                                ) : null}
                                            </>
                                        ) : location.productId && isLocationSelected ? (
                                            <LocationProductInfoRow
                                                $hideOnPrint
                                                onClick={() =>
                                                    updateSelectedLocation({
                                                        replacementProductId: location.productId,
                                                        stockToAdd: 0,
                                                    })
                                                }
                                            >
                                                <BiArrowFromTop size={20} />
                                            </LocationProductInfoRow>
                                        ) : null}

                                        {!replacementProduct?.isWithoutStock ? (
                                            <LocationProductInfoRow $hideOnPrint>
                                                {usedReassortmentType === ProductArrangementReassortmentType.Target ? (
                                                    <LocationProductInfo
                                                        color={
                                                            targetStockDisplay.isError ? theme?.errorColor : undefined
                                                        }
                                                    >
                                                        {targetStockDisplay.label}
                                                    </LocationProductInfo>
                                                ) : (
                                                    <LocationProductInfo>Manuel</LocationProductInfo>
                                                )}
                                            </LocationProductInfoRow>
                                        ) : null}
                                    </Location>
                                );
                            })}
                        </LocationRow>
                    );
                })}
            </ShelfBody>
        </Shelf>
    );
};

const Shelf = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1 0 0;
    background-color: ${({ theme }) => theme.darkBackgroundColor};
`;

const ShelfHeader = styled.div`
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};

    @media print {
        font-size: 17px;
    }
`;

const ShelfBody = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 0 0;
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 2px;

    & > :not(:first-child) {
        margin-top: 2px;
    }

    @media print {
        & > :not(:first-child) {
            margin-top: 1px;
        }
    }
`;

const LocationRow = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1 0 0;

    & > :not(:first-child) {
        margin-left: 2px;
    }

    @media print {
        & > :not(:first-child) {
            margin-left: 1px;
        }
    }
`;

const Location = styled.div<{
    $canSelectLocation: boolean;
    $isSelected: boolean;
    $backgroundColor: string;
    $hasStockToAdd?: boolean;
    $isDoubleCoil?: boolean;
}>`
    cursor: ${({ $canSelectLocation }) => ($canSelectLocation ? 'pointer' : 'not-allowed')};
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 0 26px' : '1 0 0')};
    border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    box-shadow: ${({ theme, $isSelected }) => ($isSelected ? theme.boxShadow : 'none')};
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 5px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    & > :not(:first-child) {
        margin-top: 5px;
    }

    @media print {
        padding: 1px;
        border: 1px solid
            ${({ theme, $hasStockToAdd }) => ($hasStockToAdd ? theme.successColor : theme.lightBorderColor)};

        & > :not(:first-child) {
            margin-top: 1px;
        }
    }
`;

const EmptyLocation = styled(Location)`
    min-height: 25px;
`;

const LocationProductName = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-grow: 1;
    font-size: 13px;

    @media print {
        font-size: 9px;
    }
`;

const LocationProductInfoRow = styled.div<{ $hideOnPrint?: boolean; $hideOnWeb?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    text-align: center;
    display: ${({ $hideOnWeb }) => ($hideOnWeb ? 'none' : 'initial')};

    & > :not(:first-child) {
        margin-left: 2px;
    }

    @media print {
        display: ${({ $hideOnPrint }) => ($hideOnPrint ? 'none' : 'initial')};
    }
`;

const LocationProductInfo = styled.span<{ $hideOnPrint?: boolean; $isBold?: boolean }>`
    text-align: center;
    font-size: 13px;
    color: ${({ color }) => color ?? 'inherit'};
    font-weight: ${({ $isBold }) => ($isBold ? 800 : 500)};

    @media print {
        display: ${({ $hideOnPrint }) => ($hideOnPrint ? 'none' : 'initial')};
        font-size: 9px;
    }
`;
